import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import UniverSpreadsheet from './UniverSpreadsheet'
import {
  contractItemsTableDef,
  equipmentsDetailsTableDef,
  getRentedEquipmentsTableDef,
  getExchangeRentedEquipmentsTableDef,
  contractServicesTableDef,
  billedContractServicesTableDef,
  excessContractServicesTableDef,
} from './spreadsheet/tableDefinitions'
import { getTableHeight } from '../../../../utils/spreadsheet/tableBuilder'
import { rowColToCellRef } from '../../../../utils/spreadsheet/utils'

const BillSpreadsheet = ({
  contractItems = [],
  contractServices = [],
  rentedEquipments = [],
  exchangeRentedEquipments = [],
  equipmentsDetails = [],
  monthlyEquipments = null, // array de meses da API
  monthlyData = {}, // { "2026-05": { contractItems: [], rentedEquipments: [] } }
  startDate = null, // string 'YYYY-MM-DD' para identificar o mês atual
  onReady,
  billType,
  initialSnapshot = null,
  ...props
}) => {
  const serviceEditListenerRef = useRef(null)
  // Determinar o mês atual (referência) a partir de startDate
  //
  // IMPORTANTE: não usar `new Date(startDate)` aqui. startDate é uma string
  // "YYYY-MM-DD" e o construtor Date interpreta esse formato como UTC meia-noite;
  // ao ler de volta com getFullYear()/getMonth() (que usam o fuso LOCAL do
  // navegador), em fusos atrás de UTC (ex: Brasil, UTC-3) o resultado "vaza"
  // pro dia/mês anterior (ex: "2025-01-01" vira "2024-12-31 21h" local).
  // Isso fazia currentMonthRef ficar errado (ex: "2024-12" em vez de "2025-01"),
  // a comparação com `reference` abaixo nunca batia, e o mês atual nunca era
  // filtrado da lista de meses anteriores — resultando numa aba duplicada
  // (ex: "Atual" + "Janeiro" mostrando dados diferentes pro mesmo período,
  // já que vêm de endpoints/ranges de data diferentes).
  //
  // Extraindo ano/mês direto da string, sem passar por Date, o bug some.
  const currentMonthRef = useMemo(() => {
    if (!startDate) return null
    const match = /^(\d{4})-(\d{2})/.exec(startDate)
    if (!match) return null
    return `${match[1]}-${match[2]}`
  }, [startDate])

  const rentedEquipmentsDef = useMemo(
    () => getRentedEquipmentsTableDef(billType),
    [billType]
  )

  // Definimos a coluna 13 ('N') como ponto de partida para os excedentes,
  // dando um espaço seguro após a tabela de alugados (que termina na col 10 'K')
  const exchangeEquipmentsDef = useMemo(
    () => getExchangeRentedEquipmentsTableDef(7),
    []
  )

  const billedServicesData = useMemo(
    () =>
      contractServices.map((service) => ({
        service_id: service.id,
        alias: service.alias,
        description: service.description,
        quantity: 0,
        price: Number(service.price) || 0,
      })),
    [contractServices]
  )

  const excessServicesData = useMemo(
    () =>
      contractServices.map((service) => ({
        service_id: service.id,
        alias: service.alias,
        description: service.description,
        quantity: '',
        price: Number(service.price) || 0,
      })),
    [contractServices]
  )

  const handleSpreadsheetReady = useCallback(
    (api) => {
      onReady?.(api)

      if (billType !== 'by_contract' || contractServices.length === 0) return

      serviceEditListenerRef.current?.dispose?.()

      const worksheet = api.getActiveWorkbook()?.getActiveSheet()
      const sheetId = worksheet?.getSheetId?.()
      if (!worksheet || !sheetId) return

      const billedTableStartRow =
        getTableHeight(contractServicesTableDef, contractServices) + 2
      const billedDataStartRow = billedTableStartRow + 2
      const excessTableStartRow =
        billedTableStartRow +
        getTableHeight(billedContractServicesTableDef, billedServicesData) +
        2
      const excessDataStartRow = excessTableStartRow + 2

      if (
        worksheet.getRange(billedTableStartRow, 13).getValue() !==
          billedContractServicesTableDef.title ||
        worksheet.getRange(excessTableStartRow, 13).getValue() !==
          excessContractServicesTableDef.title
      ) {
        return
      }

      serviceEditListenerRef.current = api.addEvent(
        api.Event.SheetEditEnded,
        (event) => {
          if (
            event.worksheet?.getSheetId?.() !== sheetId ||
            event.column !== 16
          ) {
            return
          }

          const serviceIndex = event.row - billedDataStartRow
          if (serviceIndex < 0 || serviceIndex >= contractServices.length) return

          const enteredValue = Number(
            event.value ??
              event.worksheet.getRange(event.row, event.column).getValue()
          )
          const requestedQuantity = Number.isFinite(enteredValue)
            ? Math.max(0, Math.floor(enteredValue))
            : 0
          const contractedQuantity = Math.max(
            0,
            Number(contractServices[serviceIndex].quantity) || 0
          )
          const billedQuantity = Math.min(requestedQuantity, contractedQuantity)
          const excessQuantity = Math.max(0, requestedQuantity - contractedQuantity)

          const quantityCell = event.worksheet.getRange(event.row, event.column)
          if (Number(quantityCell.getValue()) !== billedQuantity) {
            quantityCell.setValue(billedQuantity)
          }

          const excessQuantityCell = event.worksheet.getRange(
            excessDataStartRow + serviceIndex,
            16
          )
          const nextExcess = excessQuantity > 0 ? excessQuantity : ''
          if (excessQuantityCell.getValue() !== nextExcess) {
            excessQuantityCell.setValue(nextExcess)
          }
        }
      )
    },
    [billType, billedServicesData, contractServices, onReady]
  )

  useEffect(() => () => serviceEditListenerRef.current?.dispose?.(), [])

  // Construir configuração das sheets (abas)
  const sheetsConfig = useMemo(() => {
    if (initialSnapshot) return null

    const config = []

    // ---------- 1. Primeira aba: dados principais ----------
    const mainTables = []
    if (billType === 'separate') {
      if (rentedEquipments.length > 0) {
        mainTables.push({
          definition: rentedEquipmentsDef,
          data: rentedEquipments,
          position: 'E1',
        })
      }
      if (equipmentsDetails.length > 0) {
        mainTables.push({
          definition: equipmentsDetailsTableDef,
          data: equipmentsDetails,
          position: 'A1',
        })
      }
    } else if (billType === 'by_contract') {
      if (contractItems.length > 0) {
        mainTables.push({
          definition: contractItemsTableDef,
          data: contractItems,
          position: 'A1',
        })
      }

      if (contractServices.length > 0) {
        mainTables.push({
          definition: contractServicesTableDef,
          data: contractServices,
          position: 'N1',
        })
        mainTables.push({
          definition: billedContractServicesTableDef,
          data: billedServicesData,
          position: rowColToCellRef(
            getTableHeight(contractServicesTableDef, contractServices) + 2,
            13
          ),
        })
        mainTables.push({
          definition: excessContractServicesTableDef,
          data: excessServicesData,
          position: rowColToCellRef(
            getTableHeight(contractServicesTableDef, contractServices) +
              2 +
              getTableHeight(billedContractServicesTableDef, billedServicesData) +
              2,
            13
          ),
        })
      }

      // TIPOS ALUGADOS: SEMPRE em H1 (lado direito, mesma linha do título)
      if (rentedEquipments.length > 0) {
        mainTables.push({
          definition: rentedEquipmentsDef,
          data: rentedEquipments,
          position: 'H1',
        })
      }

      // EXCEDENTES: abaixo de TIPOS ALUGADOS (mesma coluna H)
      if (exchangeRentedEquipments.length > 0) {
        const exchangeStartRow =
          rentedEquipments.length > 0
            ? getTableHeight(rentedEquipmentsDef, rentedEquipments)
            : 0

        mainTables.push({
          definition: exchangeEquipmentsDef,
          data: exchangeRentedEquipments,
          position: rowColToCellRef(exchangeStartRow, 7), // Coluna H (índice 7)
        })
      }

      // DETALHES DOS EQUIPAMENTOS: abaixo de ITENS DO CONTRATO (lado esquerdo)
      if (equipmentsDetails.length > 0) {
        const detailsStartRow =
          contractItems.length > 0
            ? getTableHeight(contractItemsTableDef, contractItems)
            : 0

        mainTables.push({
          definition: equipmentsDetailsTableDef,
          data: equipmentsDetails,
          position: rowColToCellRef(detailsStartRow, 0), // Coluna A
        })
      }
    }

    if (mainTables.length > 0) {
      config.push({
        name: 'Atual',
        tables: mainTables,
        startPosition: { row: 0, col: 0 },
      })
    }

    // ---------- 2. Abas para meses anteriores (excluindo o mês atual) ----------
    if (monthlyEquipments && monthlyEquipments.length > 0 && currentMonthRef) {
      monthlyEquipments.forEach((monthData) => {
        const { reference, month, equipments } = monthData
        if (reference === currentMonthRef) return

        const monthExtra = monthlyData[reference] || {}
        const contractItemsForMonth = monthExtra.contractItems || []
        const rentedEquipmentsForMonth = monthExtra.rentedEquipments || []
        const exchangeRentedForMonth = monthExtra.exchangeRentedEquipments || []

        const monthTables = []

        if (billType === 'by_contract' && contractItemsForMonth.length > 0) {
          monthTables.push({
            definition: contractItemsTableDef,
            data: contractItemsForMonth,
            position: 'A1',
          })
        }

        if (rentedEquipmentsForMonth.length > 0) {
          const startCol = billType === 'by_contract' ? 'H1' : 'A1'
          monthTables.push({
            definition: rentedEquipmentsDef,
            data: rentedEquipmentsForMonth,
            position: startCol,
          })
        }

        if (exchangeRentedForMonth.length > 0 && billType === 'by_contract') {
          const exchangeStartRow =
            rentedEquipmentsForMonth.length > 0
              ? getTableHeight(rentedEquipmentsDef, rentedEquipmentsForMonth)
              : 0

          monthTables.push({
            definition: exchangeEquipmentsDef,
            data: exchangeRentedForMonth,
            position: rowColToCellRef(exchangeStartRow, 7), // Coluna H (índice 7)
          })
        }

        const equipmentsForMonth = equipments.map((item) => {
          const eq = item.equipment || item
          return {
            description: eq.description || `${eq.number} - ${eq.name}`,
            situation: eq.situation || eq.status?.name,
            movement_date: item.movement_date
              ? new Date(item.movement_date)
              : null,
          }
        })

        if (equipmentsForMonth.length > 0) {
          const contractHeight =
            contractItemsForMonth.length > 0
              ? getTableHeight(contractItemsTableDef, contractItemsForMonth)
              : 0
          const rentedHeight =
            rentedEquipmentsForMonth.length > 0
              ? getTableHeight(rentedEquipmentsDef, rentedEquipmentsForMonth)
              : 0
          const exchangeHeight =
            exchangeRentedForMonth.length > 0
              ? getTableHeight(exchangeEquipmentsDef, exchangeRentedForMonth)
              : 0
          const nextRow = Math.max(contractHeight, rentedHeight, exchangeHeight)

          monthTables.push({
            definition: equipmentsDetailsTableDef,
            data: equipmentsForMonth,
            position: rowColToCellRef(nextRow, 0),
          })
        }

        config.push({ name: month, tables: monthTables })
      })
    }

    return config.length > 0 ? config : null
  }, [
    initialSnapshot,
    billType,
    monthlyEquipments,
    currentMonthRef,
    rentedEquipments,
    equipmentsDetails,
    rentedEquipmentsDef,
    contractItems,
    contractServices,
    billedServicesData,
    excessServicesData,
    exchangeRentedEquipments,
    exchangeEquipmentsDef,
    monthlyData,
  ])

  const hasData = sheetsConfig && sheetsConfig.length > 0

  if (!hasData && !initialSnapshot) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {billType === 'separate'
              ? 'Carregando itens avulsos...'
              : 'Carregando dados do contrato...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full" key={props?.key}>
      <UniverSpreadsheet
        key={props?.key + '-spreadsheet'}
        initialSnapshot={initialSnapshot}
        sheetsConfig={sheetsConfig}
        height={700}
        onReady={handleSpreadsheetReady}
      />

      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Legenda
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
            <Typography variant="body2">
              Dados do Contrato (não editável)
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'common.white',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
            <Typography variant="body2">Campos editáveis (R$ Unit)</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#C8E6C9' }} />
            <Typography variant="body2">Saldo Positivo</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#FFCDD2' }} />
            <Typography variant="body2">Saldo Negativo (Excedente)</Typography>
          </Stack>
        </Stack>
      </Paper>
    </div>
  )
}

export default BillSpreadsheet
